// Error handling utilities
export class AppError extends Error {
  constructor(message, code, details = null) {
    super(message);
    this.name = 'AppError';
    this.code = code;
    this.details = details;
    this.timestamp = new Date().toISOString();
  }
}

export const ErrorCodes = {
  NETWORK_ERROR: 'NETWORK_ERROR',
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  AUTHENTICATION_ERROR: 'AUTHENTICATION_ERROR',
  PERMISSION_ERROR: 'PERMISSION_ERROR',
  NOT_FOUND_ERROR: 'NOT_FOUND_ERROR',
  SERVER_ERROR: 'SERVER_ERROR',
  UNKNOWN_ERROR: 'UNKNOWN_ERROR'
};

export const getErrorMessage = (error) => {
  if (error instanceof AppError) {
    return error.message;
  }
  
  if (error?.response?.data?.message) {
    return error.response.data.message;
  }
  
  if (error?.message) {
    return error.message;
  }
  
  return 'An unexpected error occurred. Please try again.';
};

export const getErrorSeverity = (error) => {
  if (error instanceof AppError) {
    switch (error.code) {
      case ErrorCodes.VALIDATION_ERROR:
        return 'warning';
      case ErrorCodes.AUTHENTICATION_ERROR:
      case ErrorCodes.PERMISSION_ERROR:
        return 'error';
      case ErrorCodes.NETWORK_ERROR:
        return 'error';
      default:
        return 'error';
    }
  }
  
  return 'error';
};

export const handleApiError = (error) => {
  if (error.name === 'TypeError' && error.message.includes('fetch')) {
    return new AppError(
      'Unable to connect to the server. Please check your internet connection.',
      ErrorCodes.NETWORK_ERROR
    );
  }
  
  if (error.status === 401) {
    return new AppError(
      'Your session has expired. Please log in again.',
      ErrorCodes.AUTHENTICATION_ERROR
    );
  }
  
  if (error.status === 403) {
    return new AppError(
      'You do not have permission to perform this action.',
      ErrorCodes.PERMISSION_ERROR
    );
  }
  
  if (error.status === 404) {
    return new AppError(
      'The requested resource was not found.',
      ErrorCodes.NOT_FOUND_ERROR
    );
  }
  
  if (error.status >= 500) {
    return new AppError(
      'Server error occurred. Please try again later.',
      ErrorCodes.SERVER_ERROR
    );
  }
  
  return new AppError(
    getErrorMessage(error),
    ErrorCodes.UNKNOWN_ERROR,
    { originalError: error }
  );
};

export const logError = (error, context = '') => {
  if (process.env.NODE_ENV === 'development') {
    console.error(`[${context}] Error:`, error);
    if (error.stack) {
      console.error('Stack trace:', error.stack);
    }
  }
  
  // In production, you might want to send errors to a logging service
  // Example: sendToLoggingService(error, context);
};
